import React, { useState, useEffect, useCallback } from 'react';
import { Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import GmailOAuthService from '../../services/gmail/GmailOAuthService';
import { parseOAuthCallback, extractOAuthError, formatOAuthError, generateOAuthState, storeOAuthState, getOAuthState, validateOAuthState } from '../../utils/oauth/oauthUtils';
import gmailConfig from '../../config/gmail';

// Environment detection helper
const isDevelopment = import.meta.env.VITE_APP_ENV !== 'staging' && import.meta.env.VITE_APP_ENV !== 'production';

// Helper function to get user-friendly error messages
const getUserFriendlyError = (technicalMessage) => {
  if (isDevelopment) {
    return technicalMessage;
  }
  
  // Map technical messages to user-friendly ones
  if (technicalMessage.includes('environment variables') || technicalMessage.includes('configuration is invalid')) {
    return 'Email connection is temporarily unavailable. Please try again later or contact support if the issue persists.';
  }
  
  if (technicalMessage.includes('Missing Google OAuth') || technicalMessage.includes('OAuth configuration')) {
    return 'Email connection service is temporarily unavailable. Please try again later.';
  }
  
  if (technicalMessage.includes('Client secret is required')) {
    return 'Email connection is temporarily unavailable. Our team has been notified and is working on a fix.';
  }
  
  // Default user-friendly message for any other technical errors
  return 'We\'re experiencing technical difficulties with email connection. Please try again later or contact support if the issue persists.';
};

// Note: Scopes are now loaded from VITE_GOOGLE_OAUTH_SCOPES environment variable
// to ensure all Google integrations (Gmail, Calendar, Contacts) use the same consolidated scopes
// This allows a single OAuth consent screen instead of multiple separate consent screens

/**
 * Gmail OAuth Component
 * Handles Google OAuth 2.0 authentication flow for Gmail access
 */
const GmailOAuth = ({
  onSuccess,
  onError,
  isLoading = false,
  buttonText = "Connect Gmail Account",
  className = "",
  buttonClassName = "",
  disabled = false,
  showConsentMessage = true,
  hideErrorDisplay = false, // When true, hides the internal error display (useful when parent handles errors)
  hideSuccessDisplay = false // When true, hides the internal success display (useful when parent handles success state)
}) => {
  const [authState, setAuthState] = useState({
    isAuthenticating: false,
    error: null,
    success: false
  });

  const [isMounted, setIsMounted] = useState(false);

  // Initialize component
  useEffect(() => {
    setIsMounted(true);
    
    // Validate configuration
    try {
      gmailConfig.validate();
    } catch (error) {
      const technicalMessage = 'Gmail OAuth configuration is invalid. Please check your environment variables.';
      setAuthState(prev => ({
        ...prev,
        error: getUserFriendlyError(technicalMessage)
      }));
    }
  }, []);

  /**
   * Handle OAuth callback from URL parameters - Enhanced with debugging
   */
  const handleOAuthCallback = useCallback(() => {
    const debugPrefix = '🔍 GmailOAuth Callback Handler:';
    
    console.log(`${debugPrefix} handleOAuthCallback called`);
    console.log(`${debugPrefix} Current URL:`, window.location.href);
    console.log(`${debugPrefix} URL search params:`, window.location.search);
    console.log(`${debugPrefix} URL hash:`, window.location.hash);
    
    const params = parseOAuthCallback();
    console.log(`${debugPrefix} Parsed OAuth callback params:`, params);
    
    if (params.code && !params.error) {
      console.log(`${debugPrefix} ✅ Found authorization code, validating state...`);
      
      // Validate OAuth state
      const storedState = getOAuthState();
      console.log(`${debugPrefix} Stored OAuth state:`, storedState);
      console.log(`${debugPrefix} Received OAuth state:`, params.state);
      
      if (!validateOAuthState(params.state, storedState)) {
        console.error(`${debugPrefix} ❌ OAuth state validation failed`);
        const errorMessage = 'Invalid OAuth state. Please try again.';
        
        // Store error in localStorage for popup communication
        localStorage.setItem('gmail_oauth_result', JSON.stringify({
          success: false,
          error: errorMessage,
          timestamp: Date.now(),
          source: 'state_validation_failed'
        }));
        
        setAuthState(prev => ({
          ...prev,
          isAuthenticating: false,
          error: errorMessage
        }));
        onError?.(errorMessage);
        return;
      }
      
      console.log(`${debugPrefix} ✅ OAuth state validation passed`);
      setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));
      processOAuthCallback(params.code);
    } else if (params.error) {
      console.error(`${debugPrefix} ❌ OAuth error in callback:`, params.error);
      
      const error = extractOAuthError(params);
      const errorMessage = formatOAuthError(error);
      
      console.log(`${debugPrefix} Formatted error message:`, errorMessage);
      
      // Store error in localStorage for popup communication
      const errorData = {
        success: false,
        error: errorMessage,
        timestamp: Date.now(),
        source: 'oauth_callback_error',
        rawError: params.error,
        errorDescription: params.error_description
      };
      
      console.log(`${debugPrefix} Storing error in localStorage:`, errorData);
      localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));

      // If this is running in a popup, close it
      if (window.opener) {
        console.log(`${debugPrefix} Running in popup - closing window with error...`);
        window.close();
        return;
      }

      // If running in main window, update state and call callback
      console.log(`${debugPrefix} Running in main window - updating state with error...`);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: errorMessage
      }));
      
      onError?.(errorMessage);
    } else {
      console.log(`${debugPrefix} No OAuth parameters found in URL`);
    }
  }, [onError]);

  // Check for OAuth params in URL after handleOAuthCallback is defined
  useEffect(() => {
    // Only handle OAuth callback if params are in URL (same-window flow)
    // For popup flow, the popup monitoring code handles it via localStorage
    const params = parseOAuthCallback();
    if (params.code || params.error) {
      console.log('🔍 GmailOAuth: OAuth params found in main window URL, processing callback');
      handleOAuthCallback();
    } else {
      console.log('🔍 GmailOAuth: No OAuth params in main window (popup flow expected)');
    }
  }, [handleOAuthCallback]);

  /**
   * Process OAuth callback with authorization code - Enhanced with debugging
   */
  const processOAuthCallback = async (code) => {
    const debugPrefix = '🔍 GmailOAuth Callback Debug:';
    
    try {
      console.log(`${debugPrefix} Processing OAuth callback with code:`, code?.substring(0, 20) + '...');
      console.log(`${debugPrefix} Running in popup:`, !!window.opener);
      console.log(`${debugPrefix} Current URL:`, window.location.href);
      console.log(`${debugPrefix} Document title:`, document.title);
      
      // Get tokens from authorization code
      console.log(`${debugPrefix} Exchanging authorization code for tokens...`);
      const tokens = await GmailOAuthService.getTokens(code);
      console.log(`${debugPrefix} Token exchange successful:`, {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in,
        tokenType: tokens.token_type
      });
      
      // Get user profile
      console.log(`${debugPrefix} Getting user profile...`);
      const profile = await GmailOAuthService.getUserProfile(tokens);
      console.log(`${debugPrefix} Profile retrieved:`, {
        email: profile.email,
        name: profile.name,
        id: profile.id,
        verified: profile.verified_email
      });
      
      // Prepare OAuth data for success callback
      const oauthData = {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        email: profile.email,
        profile: {
          id: profile.id,
          name: profile.name,
          picture: profile.picture,
          verified_email: profile.verified_email
        },
        expires_in: tokens.expires_in,
        token_type: tokens.token_type,
        scope: tokens.scope
      };

      console.log(`${debugPrefix} OAuth data prepared:`, {
        email: oauthData.email,
        hasAccessToken: !!oauthData.access_token,
        hasRefreshToken: !!oauthData.refresh_token,
        profileName: oauthData.profile.name
      });

      // Store result in localStorage for popup communication
      const resultData = {
        success: true,
        data: oauthData,
        timestamp: Date.now(),
        source: 'oauth_callback'
      };
      
      console.log(`${debugPrefix} Storing result in localStorage:`, resultData);
      localStorage.setItem('gmail_oauth_result', JSON.stringify(resultData));
      console.log(`${debugPrefix} ✅ Result stored in localStorage successfully`);

      // If this is running in a popup, close it
      if (window.opener) {
        console.log(`${debugPrefix} Running in popup - closing window...`);
        window.close();
        return;
      }

      // If running in main window, update state and call callback
      console.log(`${debugPrefix} Running in main window - updating state...`);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        success: true,
        error: null
      }));

      // Call success callback
      console.log(`${debugPrefix} Calling onSuccess callback...`);
      onSuccess?.(oauthData);

      // Clear URL parameters
      console.log(`${debugPrefix} Clearing URL parameters...`);
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log(`${debugPrefix} ✅ OAuth callback processing completed successfully`);

    } catch (error) {
      console.error(`${debugPrefix} ❌ OAuth callback processing error:`, error);
      console.error(`${debugPrefix} Error details:`, {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      const technicalMessage = error.message || 'Failed to complete Gmail authentication';
      const errorMessage = getUserFriendlyError(technicalMessage);
      
      // Store error in localStorage for popup communication
      const errorData = {
        success: false,
        error: errorMessage,
        timestamp: Date.now(),
        source: 'oauth_callback_error'
      };
      
      console.log(`${debugPrefix} Storing error in localStorage:`, errorData);
      localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
      console.log(`${debugPrefix} ✅ Error stored in localStorage`);

      // If this is running in a popup, close it
      if (window.opener) {
        console.log(`${debugPrefix} Running in popup - closing window with error...`);
        window.close();
        return;
      }

      // If running in main window, update state and call callback
      console.log(`${debugPrefix} Running in main window - updating state with error...`);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: errorMessage
      }));
      
      onError?.(errorMessage);
    }
  };

  // Check for OAuth result in localStorage on mount (handles non-popup flow where user was redirected back)
  // This can happen if popup was blocked or OAuth completed in same window
  // This useEffect must be after processOAuthCallback is defined
  useEffect(() => {
    // Skip if we already processed URL params
    const params = parseOAuthCallback();
    if (params.code || params.error) {
      return; // Already handled in the previous useEffect
    }

    const oauthResult = localStorage.getItem('gmail_oauth_result');
    if (oauthResult) {
      console.log('🔍 GmailOAuth: Found OAuth result in localStorage, processing...');
      try {
        const result = JSON.parse(oauthResult);
        
        // Only process if result is recent (within last 5 minutes)
        const resultAge = Date.now() - (result.timestamp || 0);
        const maxAge = 5 * 60 * 1000; // 5 minutes
        
        if (resultAge < maxAge) {
          console.log('🔍 GmailOAuth: OAuth result is recent, processing...');
          
          if (result.success && result.data) {
            // If we have a code, exchange it for tokens
            if (result.data.code) {
              console.log('🔍 GmailOAuth: Found authorization code in result, exchanging for tokens...');
              processOAuthCallback(result.data.code);
            } else {
              // Direct OAuth data (already processed)
              console.log('🔍 GmailOAuth: Using direct OAuth data from localStorage');
              setAuthState(prev => ({
                ...prev,
                isAuthenticating: false,
                success: true,
                error: null
              }));
              onSuccess?.(result.data);
            }
          } else if (result.error) {
            console.error('🔍 GmailOAuth: OAuth error from localStorage:', result.error);
            setAuthState(prev => ({
              ...prev,
              isAuthenticating: false,
              error: result.error || 'Authentication failed'
            }));
            onError?.(result.error || 'Authentication failed');
          }
          
          // Clean up the result after processing
          localStorage.removeItem('gmail_oauth_result');
          console.log('🔍 GmailOAuth: Cleaned up OAuth result from localStorage');
        } else {
          console.log('🔍 GmailOAuth: OAuth result is too old, ignoring');
          localStorage.removeItem('gmail_oauth_result');
        }
      } catch (parseError) {
        console.error('🔍 GmailOAuth: Failed to parse OAuth result from localStorage:', parseError);
        localStorage.removeItem('gmail_oauth_result');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount, processOAuthCallback is stable

  /**
   * Start OAuth authentication flow in same window (fallback for popup blockers)
   */
  const startOAuthFlowSameWindow = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));
      
      // Generate and store OAuth state for security
      const state = generateOAuthState();
      storeOAuthState(state);
      
      // Store the current page URL so we can redirect back after OAuth completes
      const returnUrl = window.location.pathname;
      localStorage.setItem('gmail_oauth_return_url', returnUrl);
      console.log('GmailOAuth: Stored return URL for same-window flow:', returnUrl);
      
      // Build OAuth URL with PKCE
      // Use env var scopes (consolidated for all Google integrations) instead of custom scopes
      const authUrl = await GmailOAuthService.getAuthUrl();
      const urlWithState = `${authUrl}&state=${state}`;
      
      console.log('GmailOAuth: Opening OAuth in same window:', urlWithState);
      
      // Redirect to OAuth URL in same window
      window.location.href = urlWithState;
      
    } catch (error) {
      console.error('OAuth flow error:', error);
      const technicalMessage = error.message || 'Failed to start Gmail authentication';
      const errorMessage = getUserFriendlyError(technicalMessage);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: errorMessage
      }));
      onError?.(errorMessage);
    }
  }, [onError]);

  /**
   * Start OAuth authentication flow with comprehensive debugging
   */
  const startOAuthFlow = useCallback(async () => {
    const debugPrefix = '🔍 GmailOAuth Popup Debug:';
    
    try {
      console.log(`${debugPrefix} Starting OAuth popup flow...`);
      setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));
      
      // Generate and store OAuth state for security
      const state = generateOAuthState();
      storeOAuthState(state);
      console.log(`${debugPrefix} Generated OAuth state:`, state);
      
      // Clear any previous OAuth results from localStorage
      localStorage.removeItem('gmail_oauth_result');
      localStorage.removeItem('oauth_callback_reached');
      localStorage.removeItem('oauth_callback_timestamp');
      console.log(`${debugPrefix} Cleared previous OAuth results from localStorage`);
      console.log(`${debugPrefix} Cleared callback reach flags from localStorage`);
      
      // Store the current page URL so we can redirect back after OAuth completes
      const returnUrl = window.location.pathname;
      localStorage.setItem('gmail_oauth_return_url', returnUrl);
      console.log(`${debugPrefix} Stored return URL:`, returnUrl);
      
      // Build OAuth URL with PKCE
      // Use env var scopes (consolidated for all Google integrations) instead of custom scopes
      const authUrl = await GmailOAuthService.getAuthUrl();
      const urlWithState = `${authUrl}&state=${state}`;
      
      // Log redirect URI being used (critical for debugging redirect_uri_mismatch errors)
      const redirectUri = GmailOAuthService.redirectUri;
      console.log(`${debugPrefix} Generated OAuth URL:`, urlWithState);
      console.log(`${debugPrefix} URL length:`, urlWithState.length);
      console.log(`${debugPrefix} 🔧 Redirect URI being used:`, redirectUri);
      console.warn(`${debugPrefix} ⚠️ If you see "redirect_uri_mismatch" error, add this to Google Cloud Console:`);
      console.warn(`${debugPrefix}    ${redirectUri}`);
      
      // Open OAuth popup
      console.log(`${debugPrefix} Opening popup window...`);
      const popup = window.open(
        urlWithState,
        'gmail-oauth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );
      
      console.log(`${debugPrefix} Popup object:`, popup);
      console.log(`${debugPrefix} Popup type:`, typeof popup);
      console.log(`${debugPrefix} Popup closed immediately:`, popup?.closed);
      
      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        console.error(`${debugPrefix} ❌ Popup was blocked by browser`);
        console.error(`${debugPrefix} Popup object:`, popup);
        console.error(`${debugPrefix} Popup closed:`, popup?.closed);
        console.error(`${debugPrefix} Popup type:`, typeof popup?.closed);
        
        // Automatically fall back to same-window redirect
        console.log(`${debugPrefix} 🔄 Falling back to same-window redirect method...`);
        setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));
        
        // Use same-window redirect as fallback
        try {
          const state = generateOAuthState();
          storeOAuthState(state);
          const authUrl = await GmailOAuthService.getAuthUrl(GMAIL_SCOPES);
          const urlWithState = `${authUrl}&state=${state}`;
          
          // Store the current page URL so we can redirect back after OAuth completes
          const returnUrl = window.location.pathname;
          localStorage.setItem('gmail_oauth_return_url', returnUrl);
          console.log(`${debugPrefix} Stored return URL for same-window flow:`, returnUrl);
          
          console.log(`${debugPrefix} 🔄 Redirecting to OAuth URL in same window:`, urlWithState);
          window.location.href = urlWithState;
          return; // Don't set error, let the redirect happen
        } catch (fallbackError) {
          console.error(`${debugPrefix} ❌ Fallback redirect also failed:`, fallbackError);
          setAuthState(prev => ({
            ...prev,
            isAuthenticating: false,
            error: 'Popup blocked by browser. Please allow popups for this site and try again.'
          }));
          onError?.('Popup blocked by browser. Please allow popups for this site and try again.');
          return;
        }
      }
      
      console.log(`${debugPrefix} ✅ Popup opened successfully`);
      console.log(`${debugPrefix} Popup window object:`, popup);
      console.log(`${debugPrefix} Popup location:`, popup.location?.href);
      
      // Monitor popup for completion with detailed logging and URL tracking
      let checkCount = 0;
      const maxChecks = 300; // 5 minutes max wait time
      let lastUrl = '';
      let urlChangeCount = 0;
      
      const checkClosed = setInterval(async () => {
        checkCount++;
        
        // Monitor popup URL changes
        try {
          let currentUrl = '';
          try {
            currentUrl = popup.location.href;
          } catch (e) {
            // Cross-origin error - popup is on different domain
            currentUrl = '[Cross-origin - cannot access]';
          }
          
          // Log URL changes
          if (currentUrl !== lastUrl) {
            urlChangeCount++;
            console.log(`${debugPrefix} 🔗 URL Change #${urlChangeCount}:`, currentUrl);
            console.log(`${debugPrefix} 🔗 Previous URL:`, lastUrl || '[Initial]');
            console.log(`${debugPrefix} 🔗 URL change reason:`, {
              isGoogleAuth: currentUrl.includes('accounts.google.com'),
              isCallback: currentUrl.includes('/auth/google/callback'),
              isCrossOrigin: currentUrl === '[Cross-origin - cannot access]',
              hasCode: currentUrl.includes('code='),
              hasError: currentUrl.includes('error=')
            });
            lastUrl = currentUrl;
          }
          
          // Log popup status every 10 checks (10 seconds)
          if (checkCount % 10 === 0) {
            console.log(`${debugPrefix} 📊 Status Update #${checkCount}:`, {
              popupClosed: popup.closed,
              currentUrl: currentUrl,
              urlChanges: urlChangeCount,
              timeElapsed: `${checkCount} seconds`
            });
          }
          
          // Check if we've reached the callback URL
          if (currentUrl.includes('/auth/google/callback')) {
            console.log(`${debugPrefix} 🎯 Popup reached callback URL!`);
            console.log(`${debugPrefix} 🎯 Callback URL:`, currentUrl);
            console.log(`${debugPrefix} 🎯 Waiting for callback processing...`);
            
            // Check if callback component has set the flag
            setTimeout(() => {
              const callbackReached = localStorage.getItem('oauth_callback_reached');
              const callbackTimestamp = localStorage.getItem('oauth_callback_timestamp');
              const callbackContext = localStorage.getItem('oauth_callback_context');
              console.log(`${debugPrefix} 🎯 Callback component flag check:`, callbackReached);
              console.log(`${debugPrefix} 🎯 Callback timestamp:`, callbackTimestamp);
              console.log(`${debugPrefix} 🎯 Callback context:`, callbackContext);
              
              if (callbackReached) {
                console.log(`${debugPrefix} 🎯 ✅ Callback component successfully loaded!`);
                console.log(`${debugPrefix} 🎯 ✅ Context:`, callbackContext);
              } else {
                console.log(`${debugPrefix} 🎯 ❌ Callback component did not load - routing issue!`);
                console.log(`${debugPrefix} 🎯 ❌ This suggests the callback route is not accessible or has authentication issues`);
              }
            }, 2000); // Wait 2 seconds for component to mount
          }
          
        } catch (urlError) {
          console.log(`${debugPrefix} ⚠️ URL monitoring error:`, urlError.message);
        }
        
        console.log(`${debugPrefix} Check #${checkCount}: Popup status - closed: ${popup.closed}`);
        
        if (popup.closed) {
          console.log(`${debugPrefix} ✅ Popup closed after ${checkCount} checks`);
          console.log(`${debugPrefix} 📊 Final popup statistics:`, {
            totalChecks: checkCount,
            urlChanges: urlChangeCount,
            finalUrl: lastUrl,
            timeElapsed: `${checkCount} seconds`
          });
          clearInterval(checkClosed);
          
          // Try to get final URL (may fail due to cross-origin)
          try {
            const finalUrl = popup.location.href;
            console.log(`${debugPrefix} Final popup URL:`, finalUrl);
            
            if (finalUrl.includes('/landing')) {
              console.error(`${debugPrefix} ❌ CRITICAL: Popup redirected to /landing instead of /auth/google/callback!`);
              console.error(`${debugPrefix} This explains why OAuth params are missing`);
            }
          } catch (e) {
            console.log(`${debugPrefix} Cannot access popup URL (cross-origin or closed)`);
          }
          
          // Wait a moment for popup to finish writing to localStorage
          // The OAuthCallback component waits 1 second before closing, so we should wait too
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Check if we have OAuth data in localStorage (set by popup)
          console.log(`${debugPrefix} Checking localStorage for OAuth result...`);
          let oauthResult = localStorage.getItem('gmail_oauth_result');
          console.log(`${debugPrefix} OAuth result from localStorage (first check):`, oauthResult);
          
          // If no result found, retry a few times (popup might still be writing)
          if (!oauthResult) {
            console.log(`${debugPrefix} ⏳ No result found, retrying...`);
            for (let retry = 0; retry < 3; retry++) {
              await new Promise(resolve => setTimeout(resolve, 500));
              oauthResult = localStorage.getItem('gmail_oauth_result');
              console.log(`${debugPrefix} OAuth result from localStorage (retry ${retry + 1}):`, oauthResult);
              if (oauthResult) break;
            }
          }
          
          // Check if callback component was reached
          const callbackReached = localStorage.getItem('oauth_callback_reached');
          const callbackTimestamp = localStorage.getItem('oauth_callback_timestamp');
          const callbackContext = localStorage.getItem('oauth_callback_context');
          console.log(`${debugPrefix} Callback component reached flag:`, callbackReached);
          console.log(`${debugPrefix} Callback timestamp:`, callbackTimestamp);
          console.log(`${debugPrefix} Callback context:`, callbackContext);
          
          // Log all localStorage keys for debugging
          const allKeys = Object.keys(localStorage);
          console.log(`${debugPrefix} All localStorage keys:`, allKeys);
          console.log(`${debugPrefix} localStorage size:`, localStorage.length);
          
          // Check for any OAuth-related keys
          const oauthKeys = allKeys.filter(key => key.toLowerCase().includes('oauth') || key.toLowerCase().includes('gmail'));
          console.log(`${debugPrefix} OAuth-related localStorage keys:`, oauthKeys);
          
          if (oauthResult) {
            console.log(`${debugPrefix} ✅ Found OAuth result in localStorage`);
            try {
              const result = JSON.parse(oauthResult);
              console.log(`${debugPrefix} Parsed OAuth result:`, result);
              localStorage.removeItem('gmail_oauth_result'); // Clean up
              console.log(`${debugPrefix} Cleaned up OAuth result from localStorage`);
              
              if (result.success && result.data) {
                console.log(`${debugPrefix} ✅ OAuth success, processing data:`, result.data);
                
                // If we have a code, exchange it for tokens
                if (result.data.code) {
                  console.log(`${debugPrefix} Found authorization code, exchanging for tokens...`);
                  try {
                    const tokens = await GmailOAuthService.getTokens(result.data.code);
                    console.log(`${debugPrefix} Token exchange successful:`, { 
                      hasAccessToken: !!tokens.access_token,
                      hasRefreshToken: !!tokens.refresh_token,
                      expiresIn: tokens.expires_in
                    });
                    
                    const profile = await GmailOAuthService.getUserProfile(tokens);
                    console.log(`${debugPrefix} Profile retrieved:`, { 
                      email: profile.email,
                      name: profile.name,
                      verified: profile.verified_email
                    });
                    
                    const oauthData = {
                      access_token: tokens.access_token,
                      refresh_token: tokens.refresh_token,
                      email: profile.email,
                      profile: {
                        id: profile.id,
                        name: profile.name,
                        picture: profile.picture,
                        verified_email: profile.verified_email
                      },
                      expires_in: tokens.expires_in,
                      token_type: tokens.token_type,
                      scope: tokens.scope
                    };
                    
                    console.log(`${debugPrefix} ✅ Complete OAuth data prepared:`, oauthData);
                    
                    setAuthState(prev => ({
                      ...prev,
                      isAuthenticating: false,
                      success: true,
                      error: null
                    }));
                    onSuccess?.(oauthData);
                  } catch (tokenError) {
                    console.error(`${debugPrefix} ❌ Token exchange failed:`, tokenError);
                    
                    setAuthState(prev => ({
                      ...prev,
                      isAuthenticating: false,
                      error: `Token exchange failed: ${tokenError.message}`
                    }));
                    onError?.(`Token exchange failed: ${tokenError.message}`);
                  }
                } else {
                  // Direct OAuth data (legacy)
                  console.log(`${debugPrefix} Using direct OAuth data:`, result.data);
                  setAuthState(prev => ({
                    ...prev,
                    isAuthenticating: false,
                    success: true,
                    error: null
                  }));
                  onSuccess?.(result.data);
                }
              } else {
                console.error(`${debugPrefix} ❌ OAuth failed with error:`, result.error);
                setAuthState(prev => ({
                  ...prev,
                  isAuthenticating: false,
                  error: result.error || 'Authentication failed'
                }));
                onError?.(result.error || 'Authentication failed');
              }
            } catch (parseError) {
              console.error(`${debugPrefix} ❌ Failed to parse OAuth result:`, parseError);
              console.error(`${debugPrefix} Raw OAuth result:`, oauthResult);
              
              setAuthState(prev => ({
                ...prev,
                isAuthenticating: false,
                error: 'Invalid authentication response. Please try again.'
              }));
              onError?.('Invalid authentication response. Please try again.');
            }
          } else {
            console.error(`${debugPrefix} ❌ No OAuth result found in localStorage after retries`);
            console.error(`${debugPrefix} This usually means:`);
            console.error(`${debugPrefix} 1. Popup closed before completing OAuth`);
            console.error(`${debugPrefix} 2. OAuth callback failed to store result`);
            console.error(`${debugPrefix} 3. User cancelled the OAuth flow`);
            console.error(`${debugPrefix} 4. OAuth callback URL is incorrect`);
            console.error(`${debugPrefix} 5. Callback component failed to load`);
            console.error(`${debugPrefix} 6. Timing issue - popup closed before result was stored`);
            console.error(`${debugPrefix} 📊 Popup journey summary:`, {
              urlChanges: urlChangeCount,
              reachedCallback: lastUrl.includes('/auth/google/callback'),
              finalUrl: lastUrl
            });
            
            // Check if callback was reached at all
            const callbackReached = localStorage.getItem('oauth_callback_reached');
            if (callbackReached) {
              console.error(`${debugPrefix} ⚠️ Callback component WAS reached, but no result stored`);
              console.error(`${debugPrefix} This suggests the OAuth flow completed but result wasn't saved`);
              setAuthState(prev => ({
                ...prev,
                isAuthenticating: false,
                error: 'OAuth completed but connection failed to save. Please try again or refresh the page.'
              }));
              onError?.('OAuth completed but connection failed to save. Please try again or refresh the page.');
            } else {
              console.error(`${debugPrefix} ⚠️ Callback component was NOT reached`);
              setAuthState(prev => ({
                ...prev,
                isAuthenticating: false,
                error: 'Authentication was cancelled or failed. Please try again.'
              }));
              onError?.('Authentication was cancelled or failed. Please try again.');
            }
          }
        } else if (checkCount >= maxChecks) {
          console.error(`${debugPrefix} ❌ Timeout: Popup did not close after ${maxChecks} checks (5 minutes)`);
          console.error(`${debugPrefix} 📊 Timeout statistics:`, {
            totalChecks: checkCount,
            urlChanges: urlChangeCount,
            lastKnownUrl: lastUrl,
            popupStillOpen: !popup.closed
          });
          clearInterval(checkClosed);
          
          setAuthState(prev => ({
            ...prev,
            isAuthenticating: false,
            error: 'Authentication timeout. Please try again.'
          }));
          onError?.('Authentication timeout. Please try again.');
        }
      }, 1000);
      
    } catch (error) {
      console.error(`${debugPrefix} ❌ Error starting OAuth flow:`, error);
      const technicalMessage = `Failed to start authentication: ${error.message}`;
      const errorMessage = getUserFriendlyError(technicalMessage);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: errorMessage
      }));
      onError?.(errorMessage);
    }
  }, [onError]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Reset component state
   */
  const resetState = useCallback(() => {
    setAuthState({
      isAuthenticating: false,
      error: null,
      success: false
    });
  }, []);

  // Don't render if not mounted or configuration is invalid
  if (!isMounted || !gmailConfig.oauth.clientId) {
    return null; // Hide the component entirely when not configured
  }

  const isProcessing = authState.isAuthenticating || isLoading;
  const hasError = !!authState.error;
  const isSuccess = authState.success;

  return (
    <div className={`gmail-oauth-component ${className}`}>
      {/* Consent Message */}
      {showConsentMessage && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Gmail Access Required</p>
              <p className="text-blue-700">
                We need to access your Gmail to automatically extract camp information from your emails. 
                We only read emails and will never send, delete, or modify your messages.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message - Only show if not hidden by parent */}
      {hasError && !hideErrorDisplay && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Authentication Error
              </p>
              <p className="text-sm text-red-700 mb-2">
                {authState.error}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={clearError}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {isSuccess && !hideSuccessDisplay && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
            <p className="text-sm text-green-800">
              Gmail account connected successfully!
            </p>
          </div>
        </div>
      )}

      {/* OAuth Buttons */}
      <div className="flex flex-col items-center space-y-3">
        {/* Primary OAuth Button (Popup Method with Auto-Fallback) */}
        <button
          onClick={startOAuthFlow}
          disabled={isProcessing || disabled}
          className={`
            relative inline-flex items-center justify-center px-6 py-3 
            border border-transparent text-sm font-medium rounded-lg
            transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${isProcessing || disabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : isSuccess
                ? 'bg-green-50 border-green-300 text-green-800'
                : buttonClassName || 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:ring-blue-500 shadow-sm hover:shadow-md'
            }
            min-w-[200px] h-12
          `}
        >
          {isProcessing ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              <span>Connecting...</span>
            </>
          ) : isSuccess ? (
            <>
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>Connected</span>
            </>
          ) : (
            <>
              <Mail className="h-5 w-5 mr-2" />
              <span>{buttonText}</span>
            </>
          )}
        </button>

        {/* Fallback: Same Window Method (shown if popup fails or as alternative) */}
        {hasError && authState.error?.includes('Popup blocked') && (
          <button
            onClick={startOAuthFlowSameWindow}
            disabled={isProcessing || disabled}
            className="text-xs text-blue-600 hover:text-blue-800 underline mt-2"
          >
            Or connect in this window instead
          </button>
        )}
      </div>

      {/* Reset Button (for testing/development) */}
      {process.env.NODE_ENV === 'development' && isSuccess && (
        <div className="mt-4 text-center">
          <button
            onClick={resetState}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Reset Component
          </button>
        </div>
      )}
    </div>
  );
};

export default GmailOAuth;
